// E15 Home › System health, and the engine alerts on Home, in plain words for someone who isn't
// technical: which step is broken, what's wrong, and the one thing to do about it. No job names, agent names or error
// text; those stay in Settings › Automations for whoever wants them.
import type { HumanTask, JsonObject } from "./types";
import type { StepLive } from "./process";
import { crumbOf, TASK_HOMES } from "./nav";
import { partName } from "./engineSchedule";
import { AGENT_HOME, isAccountProblem, missingAccounts, type BuildingStatus, type TownBuildingId } from "./townSignals";
import type { BuildingId, EngineTownData } from "./engineTown";

export interface HealthItem {
  id: string;
  /** fix: something stopped working. off: a module not switched on. setup: an account still to connect. */
  tone: "fix" | "off" | "setup";
  title: string;
  /** "Settings › Sending › Mailboxes": where it is, the way the menu says it. */
  where: string;
  wrong?: string;
  todo: string;
  action: { label: string; to: string };
  /** The task behind it, if any (to mark it as seen). */
  taskId?: string;
}

const NAMES: Record<string, string> = {
  hq: "Settings",
  attract: "Attract",
  find: "Find",
  network: "Network",
  connect: "Connect",
  reconnect: "ReConnect",
  crm: "CRM",
};

/** "Settings › Sending › Mailboxes" from a page address (the menu's own breadcrumb, with arrows). */
export function whereOf(path: string): string {
  return (crumbOf(path.split("?")[0]!) ?? "").replace(/ · /g, " › ");
}

function str(v: unknown): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : "";
}

function accountsPage(b: TownBuildingId, data: EngineTownData): string | null {
  return data.rooms[`${b}:accounts`]?.desks[0]?.to ?? null;
}

/** A problem task (lib/townSignals FIX_KINDS) in plain words. */
export function plainProblem(t: Pick<HumanTask, "id" | "kind" | "payload">, data: EngineTownData): HealthItem {
  const p = (t.payload ?? {}) as JsonObject;
  const agent = str(p.agent);
  const part = agent ? partName({ agent, job: str(p.job) || "daily" }) : "A part of your engine";
  const home = AGENT_HOME[agent];
  const building = home?.building ?? "hq";
  const raw = `${str(p.error)} ${str(p.note)} ${str(p.problem)}`;
  const base = { id: t.id, taskId: t.id, tone: "fix" as const };
  const open = `/ask?task=${t.id}`;

  if ((t.kind === "engine_part_failing" || t.kind === "health_alert") && isAccountProblem(raw)) {
    const to = accountsPage(building, data) ?? "/settings/automations";
    return {
      ...base,
      title: `${NAMES[building] ?? "Ogma"} can't use one of your accounts`,
      where: whereOf(to) || NAMES[building] || "Your engine",
      wrong: `The sign-in to an account it uses has expired, so ${part.toLowerCase()} has stopped. Nothing is lost: the work waits.`,
      todo: "Sign in to it again. It takes about a minute, and the waiting work carries on straight after.",
      action: { label: "Sign in again", to },
    };
  }
  if (t.kind === "engine_part_failing") {
    const n = Number(p.failures ?? 0);
    return {
      ...base,
      title: `${part} has stopped working`,
      where: home && home.office ? `${NAMES[building]} › ${home.office.replace(/^./, (c) => c.toUpperCase())}` : "Settings › Automations",
      wrong: `It didn't finish ${n > 1 ? `${n} times in a row` : "its last run"}, so it has paused. Nothing is lost: the work waits.`,
      todo: "Press Run now to try again. If it stops again, tell us and we'll look into it.",
      action: { label: "Try again", to: "/settings/automations" },
    };
  }
  if (t.kind === "health_alert") {
    const hours = typeof p.hours_since_ok === "number" ? Math.round(p.hours_since_ok) : null;
    const problem = str(p.problem);
    const wrong =
      problem === "overdue"
        ? `It hasn't finished a run${hours ? ` for ${hours} hours` : " when it should have"}, so new work is waiting.`
        : problem === "never run"
          ? "It hasn't run yet."
          : "Its last runs didn't finish.";
    return {
      ...base,
      title: `${part} is behind`,
      where: home && home.office ? `${NAMES[building]} › ${home.office.replace(/^./, (c) => c.toUpperCase())}` : "Settings › Automations",
      wrong,
      todo: "Ogma's team has been told and is on it. You can press Run now to try again yourself.",
      action: { label: "Try again", to: "/settings/automations" },
    };
  }
  if (t.kind === "budget_reached") {
    const what = p.kind === "model" ? "AI" : str(p.kind) || "spending";
    return {
      ...base,
      title: "A spending limit has been reached",
      where: "Settings › Approvals and spend",
      wrong: `You've reached your ${what} limit for ${p.period === "day" ? "today" : "this month"}, so that work has paused.`,
      todo: "Raise the limit, or leave it and the work starts again when the limit resets.",
      action: { label: "Change the limit", to: "/settings/approvals" },
    };
  }
  if (t.kind === "review_quality_failure") {
    return {
      ...base,
      title: "A spot check found a problem",
      where: "Settings › Approvals and spend",
      wrong: "A message or post broke your voice rules, so Ogma will ask you before sending that kind again.",
      todo: "Have a look at what it wrote and tell Ogma what was wrong.",
      action: { label: "Have a look", to: open },
    };
  }
  const titles: Record<string, string> = {
    booking_problem: "A booking needs you",
    whatsapp_template_problem: "WhatsApp turned down a message template",
    notetaker_problem: "The meeting notetaker had a problem",
    payment_problem: "A payment didn't go through",
    reconnect_phone_line: "Your phone line needs reconnecting",
    contact_finder_problem: "A contact finder has stopped working",
  };
  const page = (TASK_HOMES[t.kind] ?? [])[0];
  return {
    ...base,
    title: titles[t.kind] ?? "Something needs fixing",
    where: page ? whereOf(page) || "Home" : "Home",
    todo: "Open it to see what happened and what to do.",
    action: { label: "Open", to: page ?? open },
  };
}

/** Everything in System health: problems first, then modules not switched on, then accounts still to connect. */
export function healthItems(
  problems: Pick<HumanTask, "id" | "kind" | "payload">[],
  status: Partial<Record<TownBuildingId, BuildingStatus>>,
  data: EngineTownData,
  live?: Map<string, StepLive> | null,
): HealthItem[] {
  const out = problems.map((t) => plainProblem(t, data));
  for (const b of ["attract", "find", "network", "reconnect"] as BuildingId[]) {
    const s = status[b];
    const to = accountsPage(b, data);
    if (!to) continue;
    const name = NAMES[b]!;
    if (s === "off") {
      out.push({
        id: `off:${b}`,
        tone: "off",
        title: `${name} isn't switched on yet`,
        where: `${name} › Accounts`,
        todo: `Connect the accounts ${name} needs, and switch it on.`,
        action: { label: `Activate ${name}`, to },
      });
    } else if (s === "unset" || s === "on") {
      const { missing } = missingAccounts(b, data, live);
      if (!missing.length) continue;
      out.push({
        id: `setup:${b}`,
        tone: s === "unset" ? "off" : "setup",
        title: s === "unset" ? `${name} has nothing connected yet` : `${name} needs ${missing.length === 1 ? "an account" : "some accounts"} connecting`,
        where: `${name} › Accounts`,
        wrong: `Still to connect: ${missing.slice(0, 4).join(", ")}${missing.length > 4 ? ` and ${missing.length - 4} more` : ""}.`,
        todo: s === "unset" ? `Connect them and ${name} starts working.` : "Connect them so every part of it can run.",
        action: { label: s === "unset" ? `Activate ${name}` : "Connect", to },
      });
    }
  }
  return out;
}
